import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Shield, Mail, ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

export default function AdminForgotPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/admin/password-reset/request', { email });
      setSubmitted(true);
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy via-navy to-teal-deep flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="text-green-500" size={32} />
          </div>
          <h1 className="font-display text-2xl text-navy mb-4">Check Your Email</h1>
          <p className="text-text-mid mb-6">
            If an admin account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
          </p>
          <p className="text-sm text-text-light mb-6">
            The link will expire in 1 hour.
          </p>
          <Button
            onClick={() => navigate('/admin/login')}
            className="bg-navy hover:bg-navy/90 text-white rounded-full"
          >
            Return to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy to-teal-deep flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
      >
        {/* Back link */}
        <Link 
          to="/admin/login" 
          className="inline-flex items-center gap-2 text-sm text-text-mid hover:text-navy mb-6"
        >
          <ArrowLeft size={16} /> Back to login
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="text-navy" size={32} />
          </div>
          <h1 className="font-display text-2xl text-navy mb-2">Reset Password</h1>
          <p className="text-text-mid text-sm">
            Enter your admin email and we'll send you a reset link
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="rounded-xl border-seafoam"
              required
              disabled={loading}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-navy hover:bg-navy/90 text-white rounded-full py-6 font-semibold"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
